import { defineStore } from 'pinia';
import { useUser } from './user';
import { useApi } from '@/lib/api';
import { useGlobalStore } from '@/store/global-store-base';
import {
  type CustomFieldDto,
  type SelectCustomFieldOptionDto,
  CustomFieldEntityFamily,
  CustomFieldEntityType,
  isSingleSelectCustomFieldDto,
  isMultiSelectCustomFieldDto,
} from '@/ApiClient';
import { assertUnreachable, enumToValues } from '@lib/type-helpers.ts';
import { useCustomFieldGroups } from '@/store/custom-field-groups.ts';
import { useHierarchy } from '@/store/hierarchy.ts';

export const useCustomFields = defineStore('custom-fields', () => {
  const api = useApi();
  const globalStoreBase = useGlobalStore({
    load: api.customField_GetCustomFields,
    dto: 'CustomFieldDto',
    create: api.customField_AddCustomField,
    delete: api.customField_DeleteCustomField,
    signalR: 'CustomFieldUpdate',
  });
  const { loaded } = globalStoreBase;

  function forEntity(entity: CustomFieldEntityType) {
    return loaded.value.filter((field) => field.entities.includes(entity));
  }

  // A field is visible where its group is visible and its own node restrictions allow it. An empty
  // restriction means the field simply follows its group. Mirrors HierarchyService.IsCustomFieldVisible.
  function isVisible(field: CustomFieldDto, hierarchyNodeId?: number) {
    const group = useCustomFieldGroups().byId[field.groupId];
    if (!group || !useCustomFieldGroups().isVisible(group, hierarchyNodeId)) return false;

    if (field.hierarchyNodeIds.length === 0) return true;
    if (!hierarchyNodeId) return false;

    return useHierarchy()
      .getNodeAndAncestors(hierarchyNodeId)
      .some((node) => field.hierarchyNodeIds.includes(node.id));
  }

  function visibleForEntity(entity: CustomFieldEntityType, hierarchyNodeId?: number) {
    return forEntity(entity).filter((field) => isVisible(field, hierarchyNodeId));
  }

  async function addCustomFieldOption(customField: CustomFieldDto, option: string) {
    const brandId = useUser().brandId;

    const updatedField = await api.customField_AddCustomFieldSelectOption(brandId, customField.id, { value: option });

    const index = loaded.value.findIndex((cf) => cf.id === customField.id);
    if (index > -1) {
      loaded.value[index] = updatedField;
    } else {
      loaded.value.push(updatedField);
    }

    return updatedField;
  }

  function getOption(optionId: number): SelectCustomFieldOptionDto | undefined {
    return loaded.value
      .filter((f) => isSingleSelectCustomFieldDto(f) || isMultiSelectCustomFieldDto(f))
      .flatMap((f) => f.options)
      .find((o) => o.id === optionId);
  }

  function getOptionValue(optionId: number): string | undefined {
    return getOption(optionId)?.value;
  }

  return {
    ...globalStoreBase,
    forEntity,
    isVisible,
    visibleForEntity,
    addCustomFieldOption,
    getOption,
    getOptionValue,
  };
});

export function customFieldFamilyOfEntity(entityType: CustomFieldEntityType): CustomFieldEntityFamily {
  switch (entityType) {
    case CustomFieldEntityType.Product:
    case CustomFieldEntityType.Article:
    case CustomFieldEntityType.Sku:
      return CustomFieldEntityFamily.Product;

    case CustomFieldEntityType.BrandSupplier:
      return CustomFieldEntityFamily.BrandSupplier;

    case CustomFieldEntityType.Component:
      return CustomFieldEntityFamily.Component;

    case CustomFieldEntityType.Order:
    case CustomFieldEntityType.OrderDelivery:
      return CustomFieldEntityFamily.Order;

    case CustomFieldEntityType.Color:
      return CustomFieldEntityFamily.Color;

    default:
      assertUnreachable(entityType);
  }
}

export function customFieldEntitiesInFamily(family: CustomFieldEntityFamily): CustomFieldEntityType[] {
  return enumToValues(CustomFieldEntityType).filter((entity) => customFieldFamilyOfEntity(entity) === family);
}

export const readableCustomFieldEntities: Record<CustomFieldEntityType, string> = {
  [CustomFieldEntityType.Product]: 'product',
  [CustomFieldEntityType.Article]: 'article',
  [CustomFieldEntityType.Sku]: 'SKU',
  [CustomFieldEntityType.BrandSupplier]: 'supplier',
  [CustomFieldEntityType.Component]: 'component',
  [CustomFieldEntityType.Order]: 'order',
  [CustomFieldEntityType.OrderDelivery]: 'delivery',
  [CustomFieldEntityType.Color]: 'color',
};

export const readableCustomFieldTypes: Record<CustomFieldDto['type'], string> = {
  TextCustomFieldDto: 'Text',
  MoneyCustomFieldDto: 'Money',
  NumberWithUnitCustomFieldDto: 'Amount/Quantity',
  MultiSelectCustomFieldDto: 'Multi-select',
  SingleSelectCustomFieldDto: 'Select',
  PhysicalDimensionsCustomFieldDto: 'Physical dimensions',
  FilesCustomFieldDto: 'Files',
};

export function getFilterableCustomFields(
  fields: CustomFieldDto[],
  entities: CustomFieldEntityType | CustomFieldEntityType[],
  { includeSeasonal = false }: { includeSeasonal?: boolean } = {}
): CustomFieldDto[] {
  const entityArray = Array.isArray(entities) ? entities : [entities];

  return fields.filter(
    (field) =>
      (isSingleSelectCustomFieldDto(field) || isMultiSelectCustomFieldDto(field)) &&
      entityArray.some((entity) => field.entities.includes(entity)) &&
      (includeSeasonal || !useCustomFieldGroups().getById(field.groupId)?.seasonalityEnabled)
  );
}
